<script>
import AppButton from '@/components/ui/AppButton.vue'
import Icons from '@/components/Icons.vue'

export default {
	name: 'ProfilePhotoUpload',
	components: {
		AppButton,
		Icons
	},
	data() {
		return {
			avatarLoadFailed: false
		}
	},
	props: {
		avatarUrl: {
			type: String,
			default: null
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},
	emits: ['file-selected'],
	computed: {
		showAvatar() {
			return Boolean(this.avatarUrl) && !this.avatarLoadFailed
		}
	},
	watch: {
		avatarUrl() {
			this.avatarLoadFailed = false
		}
	},
	methods: {
		selectFile() {
			if (this.disabled) return
			this.$refs.fileInput?.click()
		},
		handleFile(event) {
			this.$emit('file-selected', event)
		},
		handleAvatarError() {
			this.avatarLoadFailed = true
		}
	}
}
</script>

<template>
	<section class="profile-photo" data-qa="profile-photo-upload" :class="{ 'profile-photo--filled': showAvatar }">
		<template v-if="showAvatar">
			<img class="profile-photo__image" data-qa="profile-photo-preview-image" :src="avatarUrl" :alt="$t('myArea.avatarPreviewAlt')" @error="handleAvatarError">
			<div class="profile-photo__change-action">
				<AppButton data-qa="profile-photo-change-button" variant="photo-overlay" :disabled="disabled" @click="selectFile">
					{{ $t('buttons.changePhoto') }}
				</AppButton>
			</div>
		</template>

		<div v-else class="profile-photo__empty" data-qa="profile-photo-empty">
			<Icons class="profile-photo__placeholder" name="profilePhotoPlaceholder" aria-hidden="true" />
			<h3 class="profile-photo__title">{{ $t('myArea.addProfilePhoto') }}</h3>
			<AppButton data-qa="profile-photo-select-button" variant="photo-select" :disabled="disabled" @click="selectFile">
				{{ $t('buttons.selectPhoto') }}
			</AppButton>
		</div>

		<input
			ref="fileInput"
			class="avatar__file"
			data-qa="profile-photo-file-upload"
			type="file"
			accept="image/*"
			:aria-label="$t('buttons.selectPhoto')"
			@change="handleFile"
		>
	</section>
</template>

<style lang="sass" scoped>
@use '../../assets/sass/utilities/_responsive' as *

.profile-photo
	position: relative
	display: flex
	align-items: center
	justify-content: center
	width: 100%
	aspect-ratio: 342 / 300
	padding: 2rem 1rem
	overflow: hidden
	border: 1px dashed var(--lg-dark-gray-300)
	border-radius: 1rem
	background-color: var(--lg-light-gray-100)

	&--filled
		padding: 0
		border: 0

	&__empty
		display: flex
		flex-direction: column
		align-items: center
		gap: 1.5rem
		width: 100%

	&__placeholder
		display: block
		width: 4rem
		height: 4rem
		color: var(--lg-dark-gray-300)

		:deep(svg)
			display: block
			height: 100%

	&__title
		max-width: 14rem
		margin: -0.5rem 0 0
		font-family: var(--lgei-headline)
		font-size: 1.5rem
		font-weight: 400
		line-height: 1.75rem
		text-align: center
		color: var(--club--title-default)

	&__image
		width: 100%
		height: 100%
		object-fit: cover

	&__change-action
		position: absolute
		top: 1rem
		right: 1rem

.avatar__file
	position: absolute
	width: 1px
	height: 1px
	padding: 0
	margin: -1px
	overflow: hidden
	clip: rect(0, 0, 0, 0)
	white-space: nowrap
	border: 0
</style>
